import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { deviceService, enumService, tenantService, deviceGroupService, deviceEventLogsService, bootstrapAuthLogsService } from '../services/api';
import { EditDeviceModal, AssignDeviceModal, UnassignConfirmModal } from '../components/DeviceModals';
import DeviceSimTaxiDriver from '../components/DeviceSimTaxiDriver';
import { DeviceDocImage } from '../components/DeviceDocImage';
import './DeviceDetail.css';

// In-page nav sections (clubbed, modern).
const DD_SECTIONS = [
  { id: 'dd-profile', label: 'Profile', icon: '🪪' },
  { id: 'dd-sim', label: 'SIM Card', icon: '📶' },
  { id: 'dd-vehicle', label: 'Vehicle', icon: '🚗' },
  { id: 'dd-driver', label: 'Driver', icon: '🧑‍✈️' },
  { id: 'dd-deployment', label: 'Deployment', icon: '🌐' },
  { id: 'dd-activity', label: 'Activity', icon: '📋' },
];

// Compact read-only spec (label · value).
const Spec = ({ label, value, mono, wide }) => (
  <div className={`ddx-spec ${wide ? 'ddx-spec--wide' : ''}`}>
    <span className="ddx-spec-label">{label}</span>
    <span className={`ddx-spec-value ${mono ? 'ddx-mono' : ''}`}>{value ?? '—'}</span>
  </div>
);

const DeviceDetail = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const [device, setDevice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [removingGroupId, setRemovingGroupId] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Modals state
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showUnassignModal, setShowUnassignModal] = useState(false);
  
  // Data for modals
  const [enums, setEnums] = useState({});
  const [tenants, setTenants] = useState([]);
  
  // Logs data
  const [eventLogs, setEventLogs] = useState([]);
  const [authLogs, setAuthLogs] = useState([]);

  // In-page nav: currently-visible section (scrollspy)
  const [activeSection, setActiveSection] = useState(null);

  // Fetch enums and tenants
  useEffect(() => {
    const fetchEnums = async () => {
      try {
        const response = await enumService.getAll();
        setEnums(response.enums || {});
      } catch (err) {
        console.error('Error fetching enums:', err);
      }
    };
    const fetchTenants = async () => {
      try {
        const response = await tenantService.getAll({ pageSize: 100 });
        setTenants(response.data || []);
      } catch (err) {
        console.error('Error fetching tenants:', err);
      }
    };
    fetchEnums();
    fetchTenants();
  }, []);

  const fetchDevice = useCallback(async () => {
    try {
      setLoading(true);
      const response = await deviceService.getById(deviceId, { include: 'iot,groups' });
      setDevice(response.data);
      
      // Fetch logs for this device based on hardware IDs
      if (response.data) {
        const { nodeImei, nodeSerial, nodeWidevine, ggosDevice } = response.data;
        
        // If linked to GGOS device, fetch logs by GGOS device ID
        if (ggosDevice?.id) {
          try {
            const [eventResponse, authResponse] = await Promise.all([
              deviceEventLogsService.getByDevice(ggosDevice.id, { pageSize: 10 }),
              bootstrapAuthLogsService.getAll({ 
                search: nodeImei || nodeSerial || nodeWidevine || '',
                pageSize: 10 
              }),
            ]);
            setEventLogs(eventResponse.data || []);
            setAuthLogs(authResponse.data || []);
          } catch (logErr) {
            console.error('Error fetching logs:', logErr);
          }
        } else if (nodeImei || nodeSerial || nodeWidevine) {
          // Search auth logs by hardware IDs
          try {
            const authResponse = await bootstrapAuthLogsService.getAll({ 
              search: nodeImei || nodeSerial || nodeWidevine,
              pageSize: 10 
            });
            setAuthLogs(authResponse.data || []);
          } catch (logErr) {
            console.error('Error fetching auth logs:', logErr);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching device:', err);
      setError('Failed to load device details');
    } finally {
      setLoading(false);
    }
  }, [deviceId]);

  useEffect(() => {
    fetchDevice();
  }, [fetchDevice]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => {
        setMessage(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  // Scrollspy — highlight the section currently in view within the page.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveSection(visible[0].target.id);
      },
      { rootMargin: '-15% 0px -75% 0px', threshold: 0 }
    );
    const els = DD_SECTIONS
      .map((s) => document.getElementById(s.id))
      .filter(Boolean);
    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [device, eventLogs, authLogs]);

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveSection(id);
    }
  };

  const handleUnassign = async () => {
    setActionLoading(true);
    try {
      await deviceService.unassignTenant(deviceId);
      setMessage('Device returned to inventory');
      setShowUnassignModal(false);
      fetchDevice();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to unassign device');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete "${device?.nodeName}"? This action cannot be undone.`)) {
      return;
    }
    
    setActionLoading(true);
    try {
      await deviceService.delete(deviceId);
      setMessage('Device deleted successfully');
      setTimeout(() => navigate('/devices'), 1500);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete device');
      setActionLoading(false);
    }
  };

  const handleRemoveFromGroup = async (groupId, groupName) => {
    if (!window.confirm(`Remove this device from group "${groupName}"?`)) {
      return;
    }
    
    setRemovingGroupId(groupId);
    try {
      await deviceGroupService.removeDevice(groupId, deviceId);
      setMessage(`Device removed from "${groupName}"`);
      fetchDevice();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to remove device from group');
    } finally {
      setRemovingGroupId(null);
    }
  };

  const handleTriggerGGOSRegistration = async () => {
    setActionLoading(true);
    try {
      const response = await deviceService.triggerGGOSRegistration(deviceId);
      if (response.data.alreadyRegistered) {
        setMessage(`Device is already registered as GGOS device: ${response.data.thingName}`);
      } else {
        setMessage(`Device pre-registered as GGOS device: ${response.data.thingName}`);
      }
      fetchDevice();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to trigger GGOS registration');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const formatRelativeDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const getPlatformLabel = (platformType) => {
    const labels = {
      'in_cab': '🚕 In-Cab',
      'taxi_top': '🚖 Taxi Top',
      'in_flight': '✈️ In-Flight',
      'bus_stop': '🚏 Bus Stop',
      'billboard': '📺 Billboard',
      'transit_tv': '📺 Transit TV',
      'my_devices': '📱 My Devices',
    };
    return labels[platformType] || platformType;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!device) {
    return (
      <div className="error-page">
        <h2>Device not found</h2>
        <p>The device you're looking for doesn't exist or has been deleted.</p>
        <Link to="/devices" className="btn btn-primary">
          ← Back to Devices
        </Link>
      </div>
    );
  }

  return (
    <div className="device-detail-page">
      {/* Hero */}
      <div className="ddx-hero">
        <div className="ddx-hero-main">
          <Link to="/devices" className="ddx-back">← Devices</Link>
          <h1 className="ddx-title">{device.nodeName}</h1>
          <div className="ddx-chips">
            <span className="ddx-chip ddx-chip--accent">{getPlatformLabel(device.platformType)}</span>
            <span className="ddx-chip">{device.geoMobility === 'mobile' ? '🚗 Mobile' : '📍 Stationary'}</span>
            {device.city && <span className="ddx-chip" style={{ textTransform: 'capitalize' }}>📍 {device.city}</span>}
            {device.tenantId ? (
              <Link to={`/tenants/${device.tenantId}`} className="ddx-chip ddx-chip--ok">🏢 {device.tenantName}</Link>
            ) : (
              <span className="ddx-chip ddx-chip--warn">📦 In Inventory</span>
            )}
            {device.iot?.registered ? (
              <span className={`ddx-chip ${device.iot.connectionStatus === 'online' ? 'ddx-chip--ok' : 'ddx-chip--muted'}`}>
                {device.iot.connectionStatus === 'online' ? '🟢 Online' : '⚪ Offline'}
              </span>
            ) : (
              <span className="ddx-chip ddx-chip--muted">Not registered</span>
            )}
          </div>
        </div>
        <div className="ddx-hero-actions">
          <button className="btn btn-secondary" onClick={() => setShowEditModal(true)}>✏️ Edit</button>
          <div className="actions-dropdown">
            <button className="btn btn-secondary">Actions ▾</button>
            <div className="dropdown-menu">
              {device.tenantId ? (
                <button className="dropdown-item" onClick={() => setShowUnassignModal(true)}>📦 Return to Inventory</button>
              ) : (
                <button className="dropdown-item" onClick={() => setShowAssignModal(true)}>🔗 Assign to Tenant</button>
              )}
              <button className="dropdown-item" onClick={handleTriggerGGOSRegistration} disabled={actionLoading}>🖥️ Register as GGOS Device</button>
              <button className="dropdown-item text-danger" onClick={handleDelete}>🗑️ Delete Device</button>
            </div>
          </div>
        </div>
      </div>

      {/* Floating toasts — stay in view without scrolling to top */}
      <div className="dd2-toasts">
        {message && (
          <div className="dd2-toast dd2-toast--success" onClick={() => setMessage(null)}>
            <span>✅ {message}</span>
            <button className="dd2-toast-close" aria-label="Dismiss">✕</button>
          </div>
        )}
        {error && (
          <div className="dd2-toast dd2-toast--error" onClick={() => setError(null)}>
            <span>❌ {error}</span>
            <button className="dd2-toast-close" aria-label="Dismiss">✕</button>
          </div>
        )}
      </div>

      {/* In-page layout: sticky table-of-contents + sections */}
      <div className="dd2-layout">
        <nav className="dd2-toc">
          <div className="dd2-toc-title">On this page</div>
          {DD_SECTIONS.map((s) => (
            <button
              key={s.id}
              className={`dd2-toc-link ${activeSection === s.id ? 'is-active' : ''}`}
              onClick={() => scrollToSection(s.id)}
            >
              <span className="dd2-toc-icon">{s.icon}</span>
              <span className="dd2-toc-label">{s.label}</span>
            </button>
          ))}
        </nav>

        {/* Detail Sections */}
        <div className="detail-sections">
          {/* Profile — identity, hardware, screen, label photo (clubbed) */}
          <section className="device-detail" id="dd-profile">
            <h2>🪪 Device Profile</h2>
            <div className="ddx-specs">
              <Spec label="IMEI" value={device.imei} mono />
              <Spec label="Serial No." value={device.serialNumber} mono />
              <Spec label="Widevine ID" value={device.widevineId} mono />
              <Spec label="Platform" value={getPlatformLabel(device.platformType)} />
              <Spec label="Mobility" value={device.geoMobility === 'mobile' ? '🚗 Mobile' : '📍 Stationary'} />
              <Spec label="City" value={device.city ? <span style={{ textTransform: 'capitalize' }}>{device.city}</span> : '—'} />
              <Spec label="Aspect Ratio" value={device.aspectRatio} />
              <Spec label="Screen" value={device.screenWidth > 0 ? `${device.screenWidth} × ${device.screenHeight} cm` : '—'} />
              <Spec label="Min Bid" value={`₹${device.minBid?.toFixed(2)}`} />
              <Spec label="Device ID" value={device.id} mono wide />
            </div>
            <div className="ddx-photos">
              <DeviceDocImage
                deviceId={device.id}
                docType="hardware_label"
                label="Tablet Back / Serial Label"
                url={device.hardwareLabel?.imageUrl}
                onDone={fetchDevice}
                onMessage={setMessage}
                onError={setError}
              />
            </div>
          </section>

        {/* SIM Card + Taxi & Driver */}
        <DeviceSimTaxiDriver
          device={device}
          onRefresh={fetchDevice}
          onMessage={setMessage}
          onError={setError}
        />

        {/* Deployment — tenant + IoT registration (clubbed) */}
        <section className="device-detail" id="dd-deployment">
          <h2>🌐 Deployment</h2>
          <div className="ddx-deploy">
            {/* Tenant */}
            <div className="ddx-panel">
              <div className="ddx-panel-head">
                <span>Tenant</span>
                {device.tenantId ? (
                  <button className="btn btn-secondary btn-sm" onClick={() => setShowUnassignModal(true)}>Reassign</button>
                ) : (
                  <button className="btn btn-primary btn-sm" onClick={() => setShowAssignModal(true)}>Assign</button>
                )}
              </div>
              {device.tenantId ? (
                <div className="ddx-panel-body ddx-panel-body--ok">
                  <Link to={`/tenants/${device.tenantId}`} className="ddx-strong">🏢 {device.tenantName}</Link>
                  <span className="ddx-muted">Available for this tenant's campaigns</span>
                </div>
              ) : (
                <div className="ddx-panel-body ddx-panel-body--warn">
                  <span className="ddx-strong">📦 In Inventory</span>
                  <span className="ddx-muted">Not assigned to any tenant yet</span>
                </div>
              )}
            </div>

            {/* IoT */}
            <div className="ddx-panel">
              <div className="ddx-panel-head">
                <span>IoT Registration</span>
                {device.iot?.registered ? (
                  <span className={`ddx-pill ${device.iot.connectionStatus === 'online' ? 'ddx-pill--ok' : 'ddx-pill--muted'}`}>
                    {device.iot.connectionStatus === 'online' ? '🟢 Online' : '⚪ Offline'}
                  </span>
                ) : (
                  <button className="btn btn-primary btn-sm" onClick={handleTriggerGGOSRegistration} disabled={actionLoading}>Register</button>
                )}
              </div>
              {device.iot?.registered ? (
                <div className="ddx-specs ddx-specs--tight">
                  <Spec label="Thing" value={device.iot.thingName} mono wide />
                  <Spec label="License" value={device.iot.licenseKey} mono />
                  <Spec label="Last Seen" value={formatRelativeDate(device.iot.lastConnectedAt)} />
                  <Spec label="Model" value={device.iot.deviceModel} />
                  <Spec label="OS" value={device.iot.osVersion} />
                  <Spec label="Android" value={device.iot.androidVersion} />
                </div>
              ) : (
                <div className="ddx-panel-body ddx-panel-body--muted">
                  <span className="ddx-strong">⚪ Pending registration</span>
                  <span className="ddx-muted">Will auto-register on first boot</span>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* Activity — audit, groups, and recent logs (clubbed) */}
        <section className="device-detail" id="dd-activity">
          <h2>📋 Activity</h2>
          <div className="ddx-specs ddx-specs--tight">
            <Spec label="Created By" value={device.createdByName || device.createdByEmail} />
            <Spec label="Created" value={formatDate(device.createdAt)} />
            <Spec label="Last Updated" value={formatDate(device.updatedAt)} />
          </div>

          {device.groups && device.groups.length > 0 && (
            <>
              <div className="ddx-subhead">Device Groups</div>
              <div className="ddx-groups">
                {device.groups.map((group) => (
                  <span key={group.id} className="ddx-grouptag">
                    <Link to={`/device-groups/${group.id}`}>{group.groupName}</Link>
                    <button onClick={() => handleRemoveFromGroup(group.id, group.groupName)} disabled={removingGroupId === group.id} title="Remove from group">
                      {removingGroupId === group.id ? '…' : '✕'}
                    </button>
                  </span>
                ))}
              </div>
            </>
          )}

          {eventLogs.length > 0 && (
            <>
              <div className="ddx-subhead">Recent Device Events</div>
              <div className="ddx-tablewrap">
                <table className="ddx-table">
                  <thead><tr><th>Time</th><th>Event</th><th>Source</th><th>Details</th></tr></thead>
                  <tbody>
                    {eventLogs.map((log) => (
                      <tr key={log.id}>
                        <td className="ddx-nowrap">{formatDate(log.createdAt)}</td>
                        <td>
                          <span className={`badge badge-${log.eventType?.includes('success') || log.eventType === 'device_registered' ? 'green' : log.eventType?.includes('failed') ? 'red' : 'blue'}`}>
                            {log.eventType?.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td>{log.eventSource}</td>
                        <td className="ddx-clip" title={log.eventDetails ? JSON.stringify(log.eventDetails) : undefined}>
                          {log.eventDetails ? Object.keys(log.eventDetails).slice(0, 2).map((k) => `${k}: ${log.eventDetails[k]}`).join(', ') + (Object.keys(log.eventDetails).length > 2 ? '…' : '') : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {device.ggosDevice?.id && (
                <Link to={`/device-event-logs?deviceId=${device.ggosDevice.id}`} className="ddx-link">View all events →</Link>
              )}
            </>
          )}

          {authLogs.length > 0 && (
            <>
              <div className="ddx-subhead">Bootstrap Auth Logs</div>
              <div className="ddx-tablewrap">
                <table className="ddx-table">
                  <thead><tr><th>Time</th><th>Status</th><th>CA</th><th>IP</th></tr></thead>
                  <tbody>
                    {authLogs.map((log) => (
                      <tr key={log.id}>
                        <td className="ddx-nowrap">{formatDate(log.createdAt)}</td>
                        <td>
                          <span className={`badge badge-${log.authStatus === 'success' ? 'green' : log.authStatus === 'missing' ? 'yellow' : 'red'}`}>
                            {log.authStatus === 'success' ? '✓ Success' : log.authStatus === 'missing' ? '⚠ Missing' : `✗ ${log.authStatus}`}
                          </span>
                        </td>
                        <td>{log.caName || '-'}</td>
                        <td>{log.ipAddress || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Link to={`/bootstrap-auth-logs?search=${device.nodeImei || device.nodeSerial || device.nodeWidevine || ''}`} className="ddx-link">View all auth logs →</Link>
            </>
          )}
        </section>
        </div>
      </div>

      {/* Modals */}
      {showEditModal && (
        <EditDeviceModal
          device={device}
          enums={enums}
          onClose={() => setShowEditModal(false)}
          onSuccess={(msg) => { setMessage(msg); setShowEditModal(false); fetchDevice(); }}
          onError={setError}
        />
      )}

      {showAssignModal && (
        <AssignDeviceModal
          device={device}
          tenants={tenants}
          onClose={() => setShowAssignModal(false)}
          onSuccess={(msg) => { setMessage(msg); setShowAssignModal(false); fetchDevice(); }}
          onError={setError}
        />
      )}

      {showUnassignModal && (
        <UnassignConfirmModal
          device={device}
          onClose={() => setShowUnassignModal(false)}
          onConfirm={handleUnassign}
          loading={actionLoading}
        />
      )}
    </div>
  );
};

export default DeviceDetail;
